import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { crmOrgsApi } from '../../api/crm';
import Modal from '../Modal';
import { invalidateCrm } from '../../lib/queryCache';
import type { CrmOrganization } from '../../types';

interface Props {
  org?: CrmOrganization;
  onClose: () => void;
  onSaved: (id: string) => void;
  standalone?: boolean;
}

const INDUSTRIES = ['Technology', 'Finance', 'Healthcare', 'Manufacturing', 'Retail', 'Education', 'Real Estate', 'Media', 'Transportation', 'Other'];

export default function OrganizationForm({ org, onClose, onSaved, standalone }: Props) {
  const [form, setForm] = useState({
    name: org?.name ?? '',
    domain: org?.domain ?? '',
    website: org?.website ?? '',
    phone: org?.phone ?? '',
    industry: org?.industry ?? '',
    annualRevenue: org?.annualRevenue?.toString() ?? '',
    employeeCount: org?.employeeCount?.toString() ?? '',
    notes: org?.notes ?? '',
    tags: org?.tags?.join(', ') ?? '',
  });
  const [error, setError] = useState('');

  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }));

  const queryClient = useQueryClient();

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload: any = {
        ...form,
        tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        annualRevenue: form.annualRevenue ? parseFloat(form.annualRevenue) : undefined,
        employeeCount: form.employeeCount ? parseInt(form.employeeCount) : undefined,
        domain: form.domain || undefined,
        website: form.website || undefined,
        phone: form.phone || undefined,
        industry: form.industry || undefined,
      };
      return org ? crmOrgsApi.update(org.id, payload) : crmOrgsApi.create(payload);
    },
    onSuccess: (saved) => { invalidateCrm(queryClient); onSaved(saved.id); },
    onError: (e: any) => setError(e.response?.data?.message ?? 'Failed to save'),
  });

  const content = (
    <div className="space-y-4">
      {error && <div className="text-[13px] text-err bg-primary-muted rounded-lg p-3">{error}</div>}

      <div>
        <label className="label">Name *</label>
        <input className="input w-full" value={form.name} onChange={e => set('name', e.target.value)} placeholder="Acme Corp" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="label">Website</label>
          <input className="input w-full" value={form.website} onChange={e => set('website', e.target.value)} placeholder="https://example.com" />
        </div>
        <div>
          <label className="label">Domain</label>
          <input className="input w-full" value={form.domain} onChange={e => set('domain', e.target.value)} placeholder="example.com" />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="label">Phone</label>
          <input className="input w-full" type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} />
        </div>
        <div>
          <label className="label">Industry</label>
          <select className="select w-full" value={form.industry} onChange={e => set('industry', e.target.value)}>
            <option value="">— select —</option>
            {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="label">Annual Revenue ($)</label>
          <input className="input w-full" type="number" min="0" value={form.annualRevenue} onChange={e => set('annualRevenue', e.target.value)} />
        </div>
        <div>
          <label className="label">Employee Count</label>
          <input className="input w-full" type="number" min="0" value={form.employeeCount} onChange={e => set('employeeCount', e.target.value)} />
        </div>
      </div>

      <div>
        <label className="label">Tags <span className="text-muted font-normal">(comma-separated)</span></label>
        <input className="input w-full" value={form.tags} onChange={e => set('tags', e.target.value)} placeholder="enterprise, partner" />
      </div>

      <div>
        <label className="label">Notes</label>
        <textarea className="input w-full resize-y h-20" value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Internal notes…" />
      </div>
    </div>
  );

  const footer = (
    <>
      <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
      <button className="btn btn-primary" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending || !form.name}>
        {saveMutation.isPending ? 'Saving…' : org ? 'Save Changes' : 'Create Organization'}
      </button>
    </>
  );

  if (standalone) {
    return (
      <div className="max-w-2xl flex flex-col gap-6">
        <h1 className="page-title">{org ? 'Edit Organization' : 'New Organization'}</h1>
        <div className="card">{content}</div>
        <div className="flex justify-end gap-2">{footer}</div>
      </div>
    );
  }

  return (
    <Modal open onClose={onClose} title={org ? 'Edit Organization' : 'New Organization'} width={560} busy={saveMutation.isPending} footer={footer}>
      <div className="max-h-[70vh] overflow-y-auto pr-1">{content}</div>
    </Modal>
  );
}
